import React, { useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

// Mock data for educational resources
const featuredResources = [
  {
    id: 1,
    title: "Introduction to Cybersecurity",
    description: "A comprehensive guide to cybersecurity fundamentals",
    image: "https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=800&auto=format&fit=crop",
    category: "Security",
    author: "Security Team",
    date: "June 12, 2023"
  },
  {
    id: 2,
    title: "Leadership Essentials",
    description: "Core principles for effective leadership in modern organizations",
    image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&auto=format&fit=crop",
    category: "Leadership",
    author: "Executive Team",
    date: "Feb 18, 2023"
  },
  {
    id: 3,
    title: "Cloud Architecture Fundamentals",
    description: "Essential concepts for building reliable cloud applications",
    image: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=800&auto=format&fit=crop",
    category: "Engineering",
    author: "Cloud Team",
    date: "Apr 5, 2023"
  }
];

const allResources = [
  ...featuredResources,
  {
    id: 4,
    title: "Product Management 101",
    description: "Introduction to product management methodologies",
    image: "https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=800&auto=format&fit=crop",
    category: "Product",
    author: "Product Team",
    date: "May 20, 2023"
  },
  {
    id: 5,
    title: "Data Analysis with Python",
    description: "Learn how to analyze and visualize data using Python",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop",
    category: "Engineering",
    author: "Data Science Team",
    date: "Mar 15, 2023"
  },
  {
    id: 6,
    title: "Agile Project Management",
    description: "Best practices for running agile teams and projects",
    image: "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?w=800&auto=format&fit=crop",
    category: "Project Management",
    author: "PMO Team",
    date: "Jan 28, 2023"
  },
  {
    id: 7,
    title: "Customer Journey Mapping",
    description: "Techniques for understanding and optimizing the customer journey",
    image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&auto=format&fit=crop",
    category: "Design",
    author: "UX Team",
    date: "July 10, 2023"
  }
];

// All unique categories
const categories = ["All", ...new Set(allResources.map(resource => resource.category))];

const EducationalResources = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };

  // Filter resources based on search term and selected category
  const filteredResources = allResources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || resource.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="w-full mx-auto max-w-7xl">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-white">Educational Resources</h1>
        <div className="relative w-full sm:w-auto sm:min-w-[240px] md:w-[300px]">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search resources..."
            className="w-full py-2 pl-10 pr-4 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
      </div>

      {/* Featured Resources Section */}
      <section className="mb-8 sm:mb-12">
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-white">Featured Resources</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {featuredResources.map(resource => (
            <div key={resource.id} className="bg-gray-900 rounded-lg overflow-hidden shadow-lg transition-transform duration-200 hover:-translate-y-1">
              <img src={resource.image} alt={resource.title} className="w-full h-32 sm:h-40 object-cover" />
              <div className="p-4 sm:p-5">
                <div className="text-xs font-semibold text-primary-light uppercase mb-2">{resource.category}</div>
                <h3 className="text-base sm:text-lg font-semibold text-white mb-2 line-clamp-2">{resource.title}</h3>
                <p className="text-sm text-gray-400 mb-4 line-clamp-2 sm:line-clamp-3">{resource.description}</p>
                <div className="flex justify-between items-center text-xs text-gray-500">
                  <span>{resource.author}</span>
                  <span>{resource.date}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* All Resources Section */}
      <section>
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 sm:mb-8 overflow-x-auto pb-2">
          {categories.map(category => (
            <button
              key={category}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-full transition-colors whitespace-nowrap ${
                selectedCategory === category 
                  ? 'bg-primary border-primary text-white' 
                  : 'bg-gray-800 border border-gray-700 text-gray-400 hover:bg-gray-700 hover:text-white'
              }`}
              onClick={() => handleCategorySelect(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {filteredResources.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {filteredResources.map(resource => (
              <div key={resource.id} className="bg-gray-900 rounded-lg overflow-hidden shadow-lg transition-transform duration-200 hover:-translate-y-1">
                <img src={resource.image} alt={resource.title} className="w-full h-32 sm:h-40 object-cover" />
                <div className="p-4 sm:p-5">
                  <div className="text-xs font-semibold text-primary-light uppercase mb-2">{resource.category}</div>
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-2 line-clamp-2">{resource.title}</h3>
                  <p className="text-sm text-gray-400 mb-4 line-clamp-2 sm:line-clamp-3">{resource.description}</p>
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>{resource.author}</span>
                    <span>{resource.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 sm:py-12 bg-gray-900 rounded-lg">
            <div className="text-4xl sm:text-5xl mb-4">🎓</div>
            <h3 className="text-base sm:text-lg font-medium text-white mb-2">No resources found</h3>
            <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default EducationalResources; 